import type { ComponentProps } from "react";
import cn from "classnames";
import { useGetDiagnosisCodesDisplay } from "../../../../hooks/helpers";
import styles from "../../../../modules/notes/components/NoteContent/NoteContent.module.scss";
import { formatDate, removeEnumUnderscore } from "../../../../utils/helpers";
import { IAddendumDetails } from "../../../../modules/notes/types/notes.types";
import BaseNoteContentItem from "../NoteContentItem/NoteContentItem";
import {
    SOAP_NOTE_SECTIONS,
    SOAP_PLAN_NOTE_LABELS,
} from "../../../../utils/constants";
import { AppointmentLocationTeletherapy } from "../../../../utils/types/session";
import useGetDiagnosisCodeToDisplay from "../../../../modules/notes/hooks";
import CheckIcon from "../../../../icons/Check";
import {
    AI_REVIEW_FORM_FIELDS,
    AiReviewFormField,
} from "../../../../modules/notes/helpers/aiSessionNotes";
// prettier-ignore
import {
    AiSessionNoteReviewFieldAnnotation,
} from "../../../../modules/notes/components/AiSessionNoteReview/AiSessionNoteReview";

type SoapNoteContentItemProps = Omit<
    ComponentProps<typeof BaseNoteContentItem>,
    "headingAnnotation"
>;

function NoteContentItem(props: SoapNoteContentItemProps) {
    const { fieldId } = props;
    const reviewField = AI_REVIEW_FORM_FIELDS.find(
        (field) => field === fieldId
    ) as AiReviewFormField | undefined;
    return (
        <BaseNoteContentItem
            {...props}
            headingAnnotation={
                reviewField ? (
                    <AiSessionNoteReviewFieldAnnotation field={reviewField} />
                ) : undefined
            }
        />
    );
}

interface ViewSoapNoteProps {
    noteContent: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
    };
    /**
     * Is this view for family soap note
     */
    isFamilySoap: boolean;
    fieldAddendums: {
        [key: string]: IAddendumDetails[];
    } | null;
}

export default function ViewSoapNote({
    noteContent,
    isFamilySoap,
    fieldAddendums,
}: ViewSoapNoteProps) {
    const diagonosisCodes = useGetDiagnosisCodesDisplay(
        noteContent.diagnosis_codes
    );

    const isCheckboxChecked = (checkboxValue: string, key: string) => {
        return Boolean(
            noteContent?.[key]?.find((value: string) => value === checkboxValue)
        );
    };

    return (
        <>
            {isFamilySoap ? (
                <NoteContentItem
                    headerText={SOAP_PLAN_NOTE_LABELS.isClientPresent}
                    fieldId="isClientPresent"
                    addendums={fieldAddendums?.isClientPresent}
                    content={
                        <div className={styles.checkbox}>
                            <span
                                className={cn(styles.checkbox_wrap, {
                                    [styles.checked]:
                                        noteContent.isClientPresent,
                                })}
                            >
                                <CheckIcon />
                            </span>
                            {SOAP_PLAN_NOTE_LABELS.isClientPresent}
                        </div>
                    }
                />
            ) : null}
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.appointment_location}
                fieldId="appointment_location"
                addendums={fieldAddendums?.appointment_location}
                content={
                    removeEnumUnderscore(noteContent.appointment_location) ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.delivery_option}
                fieldId="delivery_option"
                addendums={fieldAddendums?.delivery_option}
                content={noteContent.delivery_option || "--"}
            />
            {noteContent.other_delivery_option &&
            noteContent.delivery_option ===
                AppointmentLocationTeletherapy.OTHER ? (
                <NoteContentItem
                    headerText={SOAP_PLAN_NOTE_LABELS.other_delivery_option}
                    fieldId="other_delivery_option"
                    addendums={fieldAddendums?.other_delivery_option}
                    content={noteContent.other_delivery_option || "--"}
                />
            ) : null}
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.provider_location}
                fieldId="provider_location"
                addendums={fieldAddendums?.provider_location}
                content={noteContent.provider_location || "--"}
            />

            {noteContent.other_provider_location &&
            noteContent.provider_location ===
                AppointmentLocationTeletherapy.OTHER ? (
                <NoteContentItem
                    headerText={SOAP_PLAN_NOTE_LABELS.other_provider_location}
                    fieldId="other_provider_location"
                    addendums={fieldAddendums?.other_provider_location}
                    content={noteContent.other_provider_location || "--"}
                />
            ) : null}
            <NoteContentItem
                headerText={
                    SOAP_PLAN_NOTE_LABELS.consent_was_received_for_the_teletherapy_session
                }
                fieldId="consent_was_received_for_the_teletherapy_session"
                addendums={
                    fieldAddendums?.consent_was_received_for_the_teletherapy_session
                }
                content={
                    <div className={styles.checkbox}>
                        <span
                            className={cn(styles.checkbox_wrap, {
                                [styles.checked]:
                                    noteContent.consent_was_received_for_the_teletherapy_session,
                            })}
                        >
                            <CheckIcon />
                        </span>
                        {
                            SOAP_PLAN_NOTE_LABELS.consent_was_received_for_the_teletherapy_session
                        }
                    </div>
                }
            />
            <NoteContentItem
                headerText={
                    SOAP_PLAN_NOTE_LABELS.the_teletherapy_session_was_conducted_via_video
                }
                fieldId="the_teletherapy_session_was_conducted_via_video"
                addendums={
                    fieldAddendums?.the_teletherapy_session_was_conducted_via_video
                }
                content={
                    <div className={styles.checkbox}>
                        <span
                            className={cn(styles.checkbox_wrap, {
                                [styles.checked]:
                                    noteContent.the_teletherapy_session_was_conducted_via_video,
                            })}
                        >
                            <CheckIcon />
                        </span>
                        {
                            SOAP_PLAN_NOTE_LABELS.the_teletherapy_session_was_conducted_via_video
                        }
                    </div>
                }
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.diagnosis_codes}
                fieldId="diagnosis_codes"
                addendums={fieldAddendums?.diagnosis_codes}
                content={
                    diagonosisCodes && Array.isArray(diagonosisCodes) ? (
                        <div className={styles.checkboxes}>
                            {diagonosisCodes.map((code) => (
                                <div className={styles.checkbox} key={code}>
                                    <span
                                        className={cn(styles.checkbox_wrap, {
                                            [styles.checked]: true,
                                        })}
                                    >
                                        <CheckIcon />
                                    </span>
                                    {code}
                                </div>
                            ))}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.secondary_diagnosis}
                fieldId="secondary_diagnosis"
                addendums={fieldAddendums?.secondary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.secondary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.tertiary_diagnosis}
                fieldId="tertiary_diagnosis"
                addendums={fieldAddendums?.tertiary_diagnosis}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.tertiary_diagnosis
                )}
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.additional_diagnosis_one}
                fieldId="additional_diagnosis_one"
                addendums={fieldAddendums?.additional_diagnosis_one}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_one
                )}
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.additional_diagnosis_two}
                fieldId="additional_diagnosis_two"
                addendums={fieldAddendums?.additional_diagnosis_two}
                content={useGetDiagnosisCodeToDisplay(
                    noteContent.additional_diagnosis_two
                )}
            />
            <NoteContentItem
                headerText={
                    SOAP_PLAN_NOTE_LABELS.emotional_behavioural_symptoms_demonstrated
                }
                fieldId="emotional_behavioural_symptoms_demonstrated"
                addendums={
                    fieldAddendums?.emotional_behavioural_symptoms_demonstrated
                }
                content={
                    noteContent.emotional_behavioural_symptoms_demonstrated ||
                    "--"
                }
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.causing}
                fieldId="causing"
                addendums={fieldAddendums?.causing}
                content={noteContent.causing || "--"}
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.intention_for_session}
                fieldId="intention_for_session"
                addendums={fieldAddendums?.intention_for_session}
                content={noteContent.intention_for_session || "--"}
            />
            <p
                className={styles.sectionheader}
                id={SOAP_NOTE_SECTIONS["session note"]}
            >
                Session Note
            </p>
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.subjective}
                fieldId="subjective"
                addendums={fieldAddendums?.subjective}
                content={noteContent.subjective || "--"}
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.objective}
                fieldId="objective"
                addendums={fieldAddendums?.objective}
                content={noteContent.objective || "--"}
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.assessment}
                fieldId="assessment"
                addendums={fieldAddendums?.assessment}
                content={noteContent.assessment || "--"}
            />

            <p
                className={styles.sectionheader}
                id={SOAP_NOTE_SECTIONS["therapy intervention techniques"]}
            >
                Therapy Intervention Techniques
            </p>
            <NoteContentItem
                headerText={
                    SOAP_PLAN_NOTE_LABELS.cognitive_behavioral_therapies
                }
                fieldId="cognitive_behavioral_therapies"
                addendums={fieldAddendums?.cognitive_behavioral_therapies}
                content={
                    noteContent?.cognitive_behavioral_therapies?.length > 0 ? (
                        <div className={styles.checkboxes}>
                            {isCheckboxChecked(
                                "Cognitive-Behavioral therapy (CBT)",
                                "cognitive_behavioral_therapies"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Cognitive-Behavioral therapy (CBT)
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Dialectical Behavioral therapy (DBT)",
                                "cognitive_behavioral_therapies"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Dialectical Behavioral therapy (DBT)
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Modeling and skills training",
                                "cognitive_behavioral_therapies"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Modeling and skills training
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Trauma-focused CBT",
                                "cognitive_behavioral_therapies"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Trauma-focused CB
                                </div>
                            )}
                            {isCheckboxChecked(
                                "EDMR",
                                "cognitive_behavioral_therapies"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    EMDR
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Rational Emotive Behaviour therapy",
                                "cognitive_behavioral_therapies"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Rational Emotive Behaviour therapy
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Acceptance Commitment Therapy",
                                "cognitive_behavioral_therapies"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Acceptance Commitment Therapy
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Solution Based Brief Therapy",
                                "cognitive_behavioral_therapies"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Solution Based Brief Therapy
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Mindfulness Based Cognitive Therapy",
                                "cognitive_behavioral_therapies"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Mindfulness Based Cognitive Therapy
                                </div>
                            )}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={
                    SOAP_PLAN_NOTE_LABELS.relationship_based_interventions
                }
                fieldId="relationship_based_interventions"
                addendums={fieldAddendums?.relationship_based_interventions}
                content={
                    noteContent?.relationship_based_interventions?.length >
                    0 ? (
                        <div className={styles.checkboxes}>
                            {isCheckboxChecked(
                                "Attachment-oriented interventions",
                                "relationship_based_interventions"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Attachment-oriented interventions
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Parent-child interaction therapy",
                                "relationship_based_interventions"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Parent-child interaction therapy
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Parent interventions",
                                "relationship_based_interventions"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Parent interventions
                                </div>
                            )}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.other}
                fieldId="other"
                addendums={fieldAddendums?.other}
                content={
                    noteContent?.other?.length > 0 ? (
                        <div className={styles.checkboxes}>
                            {isCheckboxChecked(
                                "Client centered therapy/ Humanism",
                                "other"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Client centered therapy/ Humanism
                                </div>
                            )}
                            {isCheckboxChecked("Gestalt therapy", "other") && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Gestalt therapy
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Existential therapy",
                                "other"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Existential therapy
                                </div>
                            )}
                            {isCheckboxChecked("Feminist therapy", "other") && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Feminist therapy
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Psychodynamic therapy",
                                "other"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Psychodynamic therapy
                                </div>
                            )}
                            {isCheckboxChecked("Grief therapy", "other") && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Grief therapy
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Internal family systems (IFS)",
                                "other"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Internal family systems (IFS)
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Narrative therapy",
                                "other"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Narrative therapy
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Positive psychology",
                                "other"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Positive psychology
                                </div>
                            )}
                            {isCheckboxChecked("Psychoeducation", "other") && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Psychoeducation
                                </div>
                            )}
                            {isCheckboxChecked("Sex therapy", "other") && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Sex therapy
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Strength based theory",
                                "other"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Strength based theory
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Career Counseling",
                                "other"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Career Counseling
                                </div>
                            )}
                            {isCheckboxChecked(
                                "Multisystemic family theory",
                                "other"
                            ) && (
                                <div className={styles.checkbox}>
                                    <span
                                        className={cn(
                                            styles.checkbox_wrap,
                                            styles.checked
                                        )}
                                    >
                                        <CheckIcon />
                                    </span>
                                    Multisystemic family theory
                                </div>
                            )}
                        </div>
                    ) : (
                        "--"
                    )
                }
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.plan}
                fieldId="plan"
                addendums={fieldAddendums?.plan}
                content={noteContent.plan || "--"}
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.frequency_of_sessions}
                fieldId="frequency_of_sessions"
                addendums={fieldAddendums?.frequency_of_sessions}
                content={noteContent.frequency_of_sessions || "--"}
            />

            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.other_frequency_of_sessions}
                fieldId="other_frequency_of_sessions"
                addendums={fieldAddendums?.other_frequency_of_sessions}
                content={noteContent.other_frequency_of_sessions || "--"}
            />

            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.date_of_next_session}
                fieldId="date_of_next_session"
                addendums={fieldAddendums?.date_of_next_session}
                content={
                    noteContent.date_of_next_session
                        ? formatDate(noteContent.date_of_next_session, true)
                        : "--"
                }
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.prognosis}
                fieldId="prognosis"
                addendums={fieldAddendums?.prognosis}
                content={noteContent.prognosis || "--"}
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.client_response}
                fieldId="client_response"
                addendums={fieldAddendums?.client_response}
                content={noteContent.client_response || "--"}
            />
            <p
                className={styles.sectionheader}
                id={SOAP_NOTE_SECTIONS["treatment plan/ progress"]}
            >
                Treatment Plan/ Progress
            </p>
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.goal_one}
                fieldId="goal_one"
                addendums={fieldAddendums?.goal_one}
                content={noteContent.goal_one || "--"}
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.progress_one}
                fieldId="progress_one"
                addendums={fieldAddendums?.progress_one}
                content={noteContent.progress_one || "--"}
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.goal_two}
                fieldId="goal_two"
                addendums={fieldAddendums?.goal_two}
                content={noteContent.goal_two || "--"}
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.progress_two}
                fieldId="progress_two"
                addendums={fieldAddendums?.progress_two}
                content={noteContent.progress_two || "--"}
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.goal_three}
                fieldId="goal_three"
                addendums={fieldAddendums?.goal_three}
                content={noteContent.goal_three || "--"}
            />
            <NoteContentItem
                headerText={SOAP_PLAN_NOTE_LABELS.progress_three}
                fieldId="progress_three"
                addendums={fieldAddendums?.progress_three}
                content={noteContent.progress_three || "--"}
            />
        </>
    );
}
